import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { GanttQueue } from '../types';

type GanttInstanceData = {
  queues: GanttQueue[];
  isLoading: boolean;
  timeoutId: NodeJS.Timeout | null;
  lastUpdate: Date | null;
};

export type GanttStoreState = {
  ganttInstances: Record<string, GanttInstanceData>;
};

const getInitialGanttInstanceData = (): GanttInstanceData => {
  return {
    queues: [],
    isLoading: true,
    timeoutId: null,
    lastUpdate: null,
  };
};

export type GanttStoreActions = {
  setGanttQueues: (instanceId: string, queues: GanttQueue[]) => void;
  setIsGanttLoading: (instanceId: string, isGanttLoading: boolean) => void;
  clearGanttInstanceData: (instanceId: string) => void;
};

export const useGanttStore = create(
  immer<GanttStoreState & GanttStoreActions>((set) => {
    return {
      ganttInstances: {},
      setGanttQueues: (instanceId, ganttQueues) =>
        set((state) => {
          if (!state.ganttInstances[instanceId]) {
            state.ganttInstances[instanceId] = getInitialGanttInstanceData();
          }

          const instance = state.ganttInstances[instanceId];

          if (instance.timeoutId) {
            clearTimeout(instance.timeoutId);
          }

          state.ganttInstances[instanceId].queues = ganttQueues;
          state.ganttInstances[instanceId].lastUpdate = new Date();

          // Clear data after 3 minutes, to not show old data after a while
          const timeoutId = setTimeout(
            () => {
              set((state) => {
                state.ganttInstances[instanceId] = getInitialGanttInstanceData();
              });
            },
            3 * 60 * 1000,
          );

          instance.timeoutId = timeoutId;
        }),
      setIsGanttLoading: (instanceId, isGanttLoading) =>
        set((state) => {
          if (!state.ganttInstances[instanceId]) {
            state.ganttInstances[instanceId] = getInitialGanttInstanceData();
          }

          state.ganttInstances[instanceId].isLoading = isGanttLoading;
        }),
      clearGanttInstanceData: (instanceId) =>
        set((state) => {
          if (state.ganttInstances[instanceId]?.timeoutId) {
            clearTimeout(state.ganttInstances[instanceId].timeoutId);
          }
          state.ganttInstances[instanceId] = getInitialGanttInstanceData();
        }),
    };
  }),
);

export const selectGanttInstanceData = (instanceId: string) => (state: GanttStoreState) =>
  state.ganttInstances[instanceId] ?? getInitialGanttInstanceData();
